// Material 3 Toolbar (https://m3.material.io/components/toolbars): a row of actions in one container.
// variant: 'docked' (full width, square) | 'floating' (pill, elevated, centered by the caller)
// tone:    'standard' (surface-container) | 'vibrant' (primary-container)
// items:   [{ key, label, icon?, selected?, disabled?, onClick? }]  icon buttons with tooltips; without `icon`, the label is shown as text
// fullWidth: stretch to the container width and spread the items evenly.
// leading / trailing: optional nodes (e.g. a Fab) placed at either end. Arrow keys / Home / End move focus.
(function () {
  const { ButtonBase, Box } = MaterialUI;
  const { useRef, useEffect } = React;

  const TONES = {
    standard: { bg: 'var(--m3-surface-container)', fg: 'var(--m3-on-surface-variant)', selBg: 'var(--m3-secondary-container)', selFg: 'var(--m3-on-secondary-container)', state: 'var(--m3-on-surface-variant)' },
    vibrant: { bg: 'var(--m3-primary-container)', fg: 'var(--m3-on-primary-container)', selBg: 'var(--m3-primary)', selFg: 'var(--m3-on-primary)', state: 'var(--m3-on-primary-container)' }
  };

  function Toolbar({ items = [], variant = 'floating', tone = 'standard', label = 'Toolbar', fullWidth = false, leading, trailing, sx }) {
    const { GoogleIcon, Tooltip } = window.Components;
    const t = TONES[tone] || TONES.standard;
    const ref = useRef(null);
    const floating = variant === 'floating';
    const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';

    // Slide the selected item into view when the bar scrolls sideways (narrow screens).
    useEffect(() => {
      const el = ref.current && ref.current.querySelector('[aria-pressed="true"]');
      const bar = ref.current;
      if (!el || !bar) return;
      const b = bar.getBoundingClientRect(), r = el.getBoundingClientRect();
      if (r.left < b.left + 8 || r.right > b.right - 8) bar.scrollTo({ left: bar.scrollLeft + (r.left + r.width / 2) - (b.left + b.width / 2), behavior: 'smooth' });
    }, [items.map(i => (i.selected ? i.key : '')).join('')]);

    const onKeyDown = (e) => {
      ref.current.dataset.input = 'keyboard';
      const btns = Array.from(ref.current.querySelectorAll('button:not(:disabled)'));
      const i = btns.indexOf(document.activeElement);
      if (i < 0) return;
      const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: btns.length - 1 }[e.key];
      if (next === undefined) return;
      e.preventDefault();
      btns[(next + btns.length) % btns.length].focus();
    };

    return (
      <Box
        ref={ref}
        role="toolbar"
        aria-label={label}
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        onPointerDown={() => { ref.current.dataset.input = 'pointer'; }}
        className="m3-toolbar"
        sx={[{
          display: 'flex',
          alignItems: 'center',
          justifyContent: floating && !fullWidth ? 'center' : floating ? 'flex-start' : 'space-between',
          gap: 1,
          width: floating && !fullWidth ? 'fit-content' : '100%',
          maxWidth: '100%',
          minHeight: fullWidth ? 80 : 64,
          px: fullWidth ? 1 : floating ? 1 : 2,
          backgroundColor: t.bg,
          color: t.fg,
          borderRadius: floating ? 'var(--m3-shape-full)' : 'var(--m3-shape-none)',
          boxShadow: floating ? 'var(--m3-elevation-3)' : 'none',
          overflowX: 'auto',
          overscrollBehaviorX: 'contain',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          '&[data-input="pointer"] button:focus-visible': { outline: 'none' }
        }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {leading}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: fullWidth ? 0 : 0.5, ...(fullWidth && { flex: 1, minWidth: 'max-content' }) }}>
          {items.map(it => (
            <Tooltip key={it.key} title={it.icon ? it.label : ''}>
              <ButtonBase
                onClick={it.onClick}
                disabled={it.disabled}
                aria-label={it.label}
                aria-pressed={it.selected === undefined ? undefined : !!it.selected}
                sx={{
                  position: 'relative',
                  width: it.icon && !fullWidth ? 48 : 'auto',
                  flex: fullWidth ? '1 0 auto' : undefined,
                  minWidth: fullWidth && !it.icon ? 'calc(6ch + 40px)' : 48,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  height: fullWidth ? 64 : it.icon ? 48 : 40,
                  px: it.icon ? 0 : fullWidth ? { xs: 2, md: 2.5 } : 2.5,
                  fontFamily: "var(--wb-font-tabs, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)", // Admin › Site settings: section tabs
                  fontSynthesis: 'none',
                  fontSize: fullWidth ? '1rem' : '0.875rem',
                  lineHeight: '20px',
                  fontWeight: 500,
                  letterSpacing: '0.1px',
                  borderRadius: 'var(--m3-shape-full)',
                  color: it.selected ? t.selFg : t.fg,
                  backgroundColor: it.selected ? t.selBg : 'transparent',
                  transition: `background-color ${motion}, color ${motion}`,
                  '&::before': { content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', backgroundColor: it.selected ? t.selFg : t.state, opacity: 0, transition: `opacity ${motion}` },
                  '&:hover::before': { opacity: 'var(--m3-state-hover)' },
                  '&:focus-visible::before': { opacity: 'var(--m3-state-focus)' },
                  '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: -3 },
                  '&:active::before': { opacity: 'var(--m3-state-pressed)' },
                  '&.Mui-disabled': { opacity: 0.38 }
                }}
              >
                {it.icon
                  ? <GoogleIcon name={it.icon} size={24} filled={!!it.selected} style={{ color: 'inherit' }} />
                  : it.label}
              </ButtonBase>
            </Tooltip>
          ))}
        </Box>
        {trailing}
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.Toolbar = Toolbar;
})();
